import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClipboardList, Plus, Trash2, PackageCheck, PackageX, BellRing, Truck, AlertTriangle, Search, X, Lock, Download, CheckCheck, TrendingDown, Trophy, Paperclip, FileWarning } from "lucide-react";
import { toast } from "sonner";
import { filterActive, HeaderFilterPopover } from "@/components/SheetFilters";

const REMINDER_DAYS = 21; // 3 weeks unreceived → flag for a received reminder
const PAGE_SIZE = 100;
const CURRENCIES = ["USD", "CAD", "GBP", "EUR"];
const CUR_SYM = { USD: "US$", CAD: "CA$", GBP: "£", EUR: "€" };

const CHEERS = [
  "Every dollar saved keeps the fleet flying!",
  "Vendor negotiations paying off — nice work!",
  "That's how it's done — great saving!",
  "Stores hero move right there!",
  "The budget thanks you!",
];

const tierMsg = (score) => {
  if (!score) return "Log your first saving — every dollar counts.";
  if (score < 1000) return "Great start — keep chasing those savings.";
  if (score < 5000) return "Impressive — you're becoming a cost-saving machine.";
  if (score < 10000) return "Outstanding — serious money saved for the fleet.";
  return "Legendary — Stores hero status unlocked.";
};

const fmtMoney = (amt, cur) => `${CUR_SYM[cur] || "$"}${Number(amt).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtTotals = (totals) => {
  const entries = Object.entries(totals || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return "—";
  return entries.map(([cur, amt]) => fmtMoney(amt, cur)).join(" · ");
};

const todayIso = () => {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const ageDays = (r) => {
  const ref = r.sent_at ? String(r.sent_at).slice(0, 10) : r.date;
  if (!ref) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((today - new Date(`${ref}T00:00:00`)) / 86400000);
};
const snoozed = (r) => !!r.follow_up_until && r.follow_up_until > todayIso();
const reminderDue = (r) => r.sent === "Y" && r.received !== "Y" && ageDays(r) > REMINDER_DAYS && !snoozed(r);

const FILTER_DEFS = [
  { label: "Date", key: "date", type: "daterange" },
  { label: "PO", key: "po", type: "text" },
  { label: "Exchange", key: "exchange", type: "options", options: ["Y", "N"] },
  { label: "Vendor", key: "vendor", type: "text" },
  { label: "Base", key: "base", type: "text" },
  { label: "Description", key: "description", type: "text" },
  { label: "Approve", key: "approve", type: "options", options: ["Y", "N"] },
  { label: "Received", key: "received", type: "options", options: ["Y", "N"] },
  { label: "Raised by", key: "created_by_name", type: "options" },
];

const TABLE_COLS = [
  { label: "Date", keys: ["date"], width: "w-[9%]" },
  { label: "PO", keys: ["po"], width: "w-[7%]" },
  { label: "Exch.", keys: ["exchange"], width: "w-[6%]" },
  { label: "Vendor", keys: ["vendor"], width: "w-[9%]" },
  { label: "Base", keys: ["base"], width: "w-[7%]" },
  { label: "Description", keys: ["description"], width: "" },
  { label: "Appr.", keys: ["approve"], width: "w-[6%]" },
  { label: "Sent", keys: [], width: "w-[5%]" },
  { label: "Received", keys: ["received"], width: "w-[11%]" },
  { label: "Reduced", keys: [], width: "w-[9%]" },
  { label: "Raised by", keys: ["created_by_name"], width: "w-[9%]" },
  { label: "", keys: [], width: "w-[4%]" },
];

const YN_STYLE = (field, yes) =>
  yes ? "bg-emerald-100 text-emerald-800" : field === "received" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-500";

function YNCell({ row, field, onSave }) {
  const yes = row[field] === "Y";
  return (
    <button
      className={`px-2.5 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest transition-colors ${YN_STYLE(field, yes)}`}
      onClick={() => onSave(row, { [field]: yes ? "N" : "Y" })}
      data-testid={`po-toggle-${field}-${row.id}`}
      title={`Toggle ${field}`}
    >
      {yes ? "Y" : "N"}
    </button>
  );
}

function TextCell({ row, field, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row[field] || "");
  useEffect(() => { setVal(row[field] || ""); }, [row, field]);
  const commit = () => {
    setEditing(false);
    if ((row[field] || "") !== val) onSave(row, { [field]: val });
  };
  if (!editing) {
    return (
      <button
        className="w-full text-left px-2 py-1 rounded hover:bg-summit-glacier/60 text-sm min-h-[28px] truncate"
        onClick={() => setEditing(true)}
        data-testid={`po-cell-${field}-${row.id}`}
        title="Click to edit"
      >
        {row[field] || <span className="text-gray-300">—</span>}
      </button>
    );
  }
  return (
    <input
      autoFocus
      className="w-full px-2 py-1 rounded border border-summit-primary text-sm outline-none"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") { setVal(row[field] || ""); setEditing(false); }
      }}
      data-testid={`po-input-${field}-${row.id}`}
    />
  );
}

function ReducedCell({ row, onSave }) {
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState(row.reduced_cost || "");
  const [cur, setCur] = useState(row.reduced_currency || "CAD");
  useEffect(() => { setAmt(row.reduced_cost || ""); setCur(row.reduced_currency || "CAD"); }, [row]);
  const save = () => {
    setOpen(false);
    onSave(row, { reduced_cost: amt === "" ? 0 : Number(amt), reduced_currency: cur });
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="w-full text-left px-2 py-1 rounded hover:bg-summit-glacier/60 min-h-[28px]"
          data-testid={`po-cell-reduced-${row.id}`}
          title="Cost saved on this PO — click to edit"
        >
          {row.reduced_cost > 0 ? (
            <span className="inline-flex items-center gap-1 font-mono text-xs text-emerald-700 font-semibold">
              <TrendingDown className="w-3 h-3" /> −{fmtMoney(row.reduced_cost, row.reduced_currency || "CAD")}
            </span>
          ) : (
            <span className="text-gray-300 text-sm">—</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="start">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Cost reduced</div>
        <div className="flex gap-2">
          <select
            value={cur}
            onChange={(e) => setCur(e.target.value)}
            className="h-8 px-1.5 rounded-md border border-summit-hairline bg-white text-xs"
            data-testid={`po-reduced-currency-${row.id}`}
          >
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input
            className="h-8 text-sm"
            type="number"
            min="0"
            step="0.01"
            placeholder="Amount"
            value={amt}
            onChange={(e) => setAmt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            data-testid={`po-reduced-input-${row.id}`}
          />
        </div>
        <Button size="sm" className="mt-2 w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid={`po-reduced-save-${row.id}`}>
          Save saving
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function AttachmentClip({ row, onChanged }) {
  const fileRef = React.useRef(null);
  const [open, setOpen] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/purchase-orders/${row.id}/attachment`, fd);
      onChanged(r.data);
      setOpen(false);
      toast.success("Supporting file attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };
  const download = async () => {
    try {
      const resp = await fetch(`${API_BASE}/purchase-orders/${row.id}/attachment`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.attachment?.filename || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };
  const removeFile = async () => {
    try {
      await api.delete(`/purchase-orders/${row.id}/attachment`);
      onChanged({ ...row, attachment: null });
      setOpen(false);
      toast.success("Attachment removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };
  return (
    <>
      <input ref={fileRef} type="file" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`po-attach-input-${row.id}`} />
      {row.attachment ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button className="p-1 rounded-md text-emerald-700 hover:bg-emerald-50 align-middle shrink-0" data-testid={`po-attach-${row.id}`} title={`Attached: ${row.attachment.filename}`}>
              <Paperclip className="w-3.5 h-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-60 p-3" align="start">
            <div className="text-xs font-medium truncate" data-testid={`po-attach-name-${row.id}`}>{row.attachment.filename}</div>
            <div className="font-mono text-[10px] text-gray-400 mt-0.5">
              {Math.max(1, Math.round((row.attachment.size || 0) / 1024))} KB · {row.attachment.uploaded_by_name}
            </div>
            <div className="flex gap-1.5 mt-2">
              <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={download} data-testid={`po-attach-download-${row.id}`}>Download</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => fileRef.current?.click()} data-testid={`po-attach-replace-${row.id}`}>Replace</Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={removeFile} data-testid={`po-attach-delete-${row.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <button className="p-1 rounded-md text-gray-300 hover:text-gray-500 hover:bg-summit-glacier align-middle shrink-0" onClick={() => fileRef.current?.click()} data-testid={`po-attach-${row.id}`} title="Attach a supporting file">
          <Paperclip className="w-3.5 h-3.5" />
        </button>
      )}
    </>
  );
}

const monthLabel = (ym) => new Date(`${ym}-01T00:00:00`).toLocaleString("en-CA", { month: "long", year: "numeric" });
const isBlankRow = (r) =>
  !(r.po || "").trim() && !(r.vendor || "").trim() && !(r.base || "").trim() && !(r.description || "").trim();

const groupByMonth = (list) => {
  const map = {};
  list.forEach((r) => {
    const ym = (r.date || "").slice(0, 7) || "Unknown";
    (map[ym] = map[ym] || []).push(r);
  });
  return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
};
const COLS = 12;

const STAT_TONES = { gray: "text-gray-900", amber: "text-amber-700", rose: "text-rose-700" };

function StatCell({ testid, icon, label, value, tone = "gray", breakdown = [] }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="px-4 py-2.5 text-left hover:bg-summit-glacier/40 transition-colors" data-testid={testid} title="Click for by-user breakdown">
          <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-gray-500">{icon} {label}</div>
          <div className={`font-display text-2xl font-semibold leading-tight mt-0.5 ${STAT_TONES[tone]}`}>{value}</div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="start" data-testid={`${testid}-breakdown`}>
        <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mb-1.5">{label} — by user</div>
        {breakdown.length === 0 ? (
          <p className="text-xs text-gray-400">Nothing here.</p>
        ) : breakdown.map(([n, c]) => (
          <div key={n} className="flex justify-between gap-2 font-mono text-[11px] text-gray-600 py-0.5">
            <span className="truncate">{n}</span><span className="shrink-0">{c}</span>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function MelUpdateDialog({ open, onOpenChange }) {
  const [items, setItems] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    api.get("/maintenance/aircraft").then((r) => {
      const flat = [];
      (r.data || []).forEach((a) => (a.mel_items || []).forEach((m) => flat.push({ tail: a.tail, ...m })));
      setItems(flat);
      setDrafts(Object.fromEntries(flat.map((m) => [m.id, m.comment || ""])));
    }).catch(() => setItems([]));
  }, [open]);

  const save = async (m) => {
    setSavingId(m.id);
    try {
      await api.patch(`/maintenance/aircraft/${m.tail}/mel/${m.id}`, { comment: drafts[m.id] });
      toast.success(`${m.tail} — MEL comment updated`);
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, comment: (drafts[m.id] || "").trim() } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update comment");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="po-mel-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><FileWarning className="w-4 h-4 text-amber-500" /> Update MEL comments</DialogTitle>
          <DialogDescription>Comments show on the fleet board MEL Items table.</DialogDescription>
        </DialogHeader>
        {items === null ? (
          <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center" data-testid="po-mel-empty">No open MEL items.</p>
        ) : (
          <div className="space-y-3">
            {items.map((m) => (
              <div key={m.id} className="border border-amber-200 rounded-md bg-amber-50/40 p-2.5" data-testid={`po-mel-row-${m.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs font-bold">{m.tail}</span>
                  <span className="px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-950 font-mono text-[9px] uppercase tracking-widest">CAT {m.category}</span>
                  {m.mel_ref && <span className="font-mono text-[10px] text-gray-500">{m.mel_ref}</span>}
                  {m.next_calendar_due && <span className="ml-auto font-mono text-[10px] text-gray-400">exp {m.next_calendar_due}</span>}
                </div>
                <div className="text-xs text-gray-700 mt-1 leading-snug">{m.description}</div>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    className="h-8 text-xs bg-white"
                    placeholder="Comment…"
                    value={drafts[m.id] ?? ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") save(m); }}
                    data-testid={`po-mel-comment-${m.id}`}
                  />
                  <Button
                    size="sm"
                    className="h-8 bg-summit-primary hover:bg-summit-primary-soft text-white shrink-0"
                    disabled={savingId === m.id || (drafts[m.id] ?? "") === (m.comment || "")}
                    onClick={() => save(m)}
                    data-testid={`po-mel-save-${m.id}`}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_AOG_FORM = { part: "", ship_to: "", eta: "", pr_po: "", comment: "" };

function AogAddDialog({ open, onOpenChange }) {
  const [aircraft, setAircraft] = useState(null);
  const [tail, setTail] = useState("");
  const [form, setForm] = useState({ ...EMPTY_AOG_FORM });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAircraft(null);
    setForm({ ...EMPTY_AOG_FORM });
    api.get("/maintenance/aircraft").then((r) => {
      setAircraft(r.data || []);
      const us = (r.data || []).filter((a) => a.status === "unserviceable");
      setTail(us[0]?.tail || "");
    }).catch(() => setAircraft([]));
  }, [open]);

  const usList = (aircraft || []).filter((a) => a.status === "unserviceable");
  const selected = usList.find((a) => a.tail === tail);

  const add = async () => {
    if (!tail) { toast.error("Pick an unserviceable aircraft"); return; }
    if (!form.part.trim()) { toast.error("Part is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/maintenance/aircraft/${tail}/aog-parts`, form);
      toast.success(`${tail} — AOG part on order added`);
      setAircraft((list) => (list || []).map((a) => (a.tail === tail ? r.data : a)));
      setForm({ ...EMPTY_AOG_FORM });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add AOG part");
    } finally {
      setBusy(false);
    }
  };

  const markReceived = async (p) => {
    if (!window.confirm(`Mark "${p.part}" received? It will drop off the fleet board.`)) return;
    try {
      const r = await api.delete(`/maintenance/aircraft/${tail}/aog-parts/${p.id}`);
      toast.success(`${tail} — "${p.part}" received`);
      setAircraft((list) => (list || []).map((a) => (a.tail === tail ? r.data : a)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark received");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="po-aog-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" /> AOG parts on order</DialogTitle>
          <DialogDescription>Parts show on the fleet board AOG Parts on Order table.</DialogDescription>
        </DialogHeader>
        {aircraft === null ? (
          <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
        ) : usList.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center" data-testid="po-aog-empty">No unserviceable aircraft — AOG parts attach to unserviceable (AOG) aircraft.</p>
        ) : (
          <div className="space-y-3">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Aircraft *</span>
              <select className="mt-1 h-10 w-full px-2 rounded-md border border-summit-hairline bg-white text-sm" value={tail} onChange={(e) => setTail(e.target.value)} data-testid="po-aog-tail">
                {usList.map((a) => <option key={a.tail} value={a.tail}>{a.tail}{a.us_reason ? ` — ${a.us_reason.slice(0, 50)}` : ""}</option>)}
              </select>
            </div>
            {(selected?.aog_parts || []).length > 0 && (
              <div className="border border-red-200 rounded-md bg-red-50/40 p-2.5 space-y-1" data-testid="po-aog-existing">
                <div className="font-mono text-[9px] uppercase tracking-widest text-red-800">Already on order</div>
                {selected.aog_parts.map((p) => (
                  <div key={p.id} className="font-mono text-[11px] text-gray-700 flex items-center gap-2 flex-wrap" data-testid={`po-aog-existing-${p.id}`}>
                    <span className="font-semibold">{p.part}</span>
                    {p.ship_to && <span className="text-gray-500">→ {p.ship_to}</span>}
                    {p.eta && <span className="text-gray-500">ETA {p.eta}</span>}
                    {p.pr_po && <span className="text-gray-500">{p.pr_po}</span>}
                    <button
                      type="button"
                      className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 hover:underline shrink-0"
                      onClick={() => markReceived(p)}
                      title="Mark received — removes it from the fleet board"
                      data-testid={`po-aog-received-${p.id}`}
                    >
                      <CheckCheck className="w-3 h-3" /> Received
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Part # / description *</span>
              <Input className="mt-1" placeholder="e.g. Chip detector P/N 3021-45" value={form.part} onChange={(e) => setForm((f) => ({ ...f, part: e.target.value }))} data-testid="po-aog-part" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Ship to</span>
                <Input className="mt-1" placeholder="e.g. YZF" value={form.ship_to} onChange={(e) => setForm((f) => ({ ...f, ship_to: e.target.value }))} data-testid="po-aog-shipto" />
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">ETA</span>
                <Input className="mt-1" type="date" value={form.eta} onChange={(e) => setForm((f) => ({ ...f, eta: e.target.value }))} data-testid="po-aog-eta" />
              </div>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">PR/PO#</span>
                <Input className="mt-1" placeholder="e.g. PO-1234" value={form.pr_po} onChange={(e) => setForm((f) => ({ ...f, pr_po: e.target.value }))} data-testid="po-aog-prpo" />
              </div>
            </div>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Comment</span>
              <Input className="mt-1" placeholder="e.g. AOG shipment via courier" value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} data-testid="po-aog-comment" />
            </div>
            <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={add} disabled={busy} data-testid="po-aog-submit">Add AOG part</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function POOverview() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [savings, setSavings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("open");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [exportMonth, setExportMonth] = useState("");
  const [melOpen, setMelOpen] = useState(false);
  const [aogOpen, setAogOpen] = useState(false);
  useEffect(() => { setPage(1); }, [tab, search, filters]);

  const hasAccess = user && (user.po_overview_access || user.role === "admin");

  const load = async () => {
    try {
      const r = await api.get("/purchase-orders");
      setRows(r.data || []);
    } catch (e) {
      setRows([]);
      toast.error(e?.response?.data?.detail || "Failed to load POs");
    }
  };
  const loadSavings = async () => {
    try {
      const r = await api.get("/purchase-orders/savings");
      setSavings(r.data);
    } catch {
      /* non-blocking */
    }
  };
  useEffect(() => { if (hasAccess) { load(); loadSavings(); } }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const addRow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/purchase-orders");
      setRows((rs) => [r.data, ...(rs || [])]);
      setTab("open");
      toast.success("PO row added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add PO");
    } finally {
      setBusy(false);
    }
  };

  const saveCell = async (row, patch) => {
    setRows((rs) => (rs || []).map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
    try {
      const r = await api.patch(`/purchase-orders/${row.id}`, patch);
      setRows((rs) => (rs || []).map((x) => (x.id === row.id ? r.data : x)));
      setBlankCheck((n) => n + 1);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
      load();
      return false;
    }
  };

  // Keep one blank line ready in the Open tab — auto-added after a line gets used.
  const [blankCheck, setBlankCheck] = useState(0);
  const autoAdding = useRef(false);
  useEffect(() => {
    if (!blankCheck || autoAdding.current) return;
    const hasBlank = (rows || []).some((r) => r.sent !== "Y" && r.received !== "Y" && isBlankRow(r));
    if (hasBlank) return;
    autoAdding.current = true;
    api.post("/purchase-orders")
      .then((r) => setRows((rs) => [...(rs || []), r.data]))
      .catch(() => {})
      .finally(() => { autoAdding.current = false; });
  }, [blankCheck]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveReduction = async (row, patch) => {
    const ok = await saveCell(row, patch);
    if (!ok) return;
    if (patch.reduced_cost > 0) {
      const msg = CHEERS[Math.floor(Math.random() * CHEERS.length)];
      toast.success(`Saved ${fmtMoney(patch.reduced_cost, patch.reduced_currency)} — ${msg}`, { duration: 5000 });
    }
    loadSavings();
  };

  const followUp = async (row) => {
    try {
      const r = await api.post(`/purchase-orders/${row.id}/follow-up`);
      setRows((rs) => (rs || []).map((x) => (x.id === row.id ? r.data : x)));
      toast.success(`Followed up — reminder pushed 2 weeks (due ${r.data.follow_up_until})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record follow-up");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete PO row${row.po ? ` "${row.po}"` : ""}?`)) return;
    try {
      await api.delete(`/purchase-orders/${row.id}`);
      setRows((rs) => (rs || []).filter((r) => r.id !== row.id));
      toast.success("Deleted");
      loadSavings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const doExport = async () => {
    if (!exportMonth) return;
    try {
      const resp = await fetch(`${API_BASE}/purchase-orders/export?month=${exportMonth}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PO-Overview-${exportMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${monthLabel(exportMonth)} PO sheet`);
    } catch {
      toast.error("Failed to export PO sheet");
    }
  };

  const openRows = useMemo(() => (rows || []).filter((r) => r.sent !== "Y" && r.received !== "Y"), [rows]);
  const sentRows = useMemo(() => (rows || []).filter((r) => r.sent === "Y" && r.received !== "Y"), [rows]);
  const receivedRows = useMemo(() => (rows || []).filter((r) => r.received === "Y"), [rows]);
  const reminderCount = useMemo(() => (rows || []).filter(reminderDue).length, [rows]);
  const notReceived = useMemo(() => (rows || []).filter((r) => r.received !== "Y").length, [rows]);

  const byUser = (list) => {
    const m = {};
    list.forEach((r) => { const n = r.created_by_name || "Unknown"; m[n] = (m[n] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };
  const raisedByUser = useMemo(() => byUser(rows || []), [rows]);
  const notReceivedByUser = useMemo(() => byUser((rows || []).filter((r) => r.received !== "Y")), [rows]);
  const reminderByUser = useMemo(() => byUser((rows || []).filter(reminderDue)), [rows]);

  const nameOptions = useMemo(() => ({
    created_by_name: [...new Set((rows || []).map((r) => r.created_by_name).filter(Boolean))].sort(),
  }), [rows]);

  const monthOptions = useMemo(() => {
    const set = [...new Set((rows || []).map((r) => (r.date || "").slice(0, 7)).filter(Boolean))];
    return set.sort((a, b) => b.localeCompare(a));
  }, [rows]);
  useEffect(() => { if (!exportMonth && monthOptions.length) setExportMonth(monthOptions[0]); }, [monthOptions, exportMonth]);

  const applyFilters = (list) => {
    let out = list;
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) => [r.po, r.vendor, r.base, r.description, r.created_by_name].some((v) => (v || "").toLowerCase().includes(q)));
    }
    for (const def of FILTER_DEFS) {
      const v = filters[def.key];
      if (!filterActive(def, v)) continue;
      out = out.filter((r) => {
        if (def.type === "text") return (r[def.key] || "").toLowerCase().includes(v.trim().toLowerCase());
        if (def.type === "options") return v.includes(r[def.key]);
        if (def.type === "daterange") return (!v.from || (r.date || "") >= v.from) && (!v.to || (r.date || "") <= v.to);
        return true;
      });
    }
    return out;
  };
  const tabRows = tab === "open" ? openRows : tab === "sent" ? sentRows : receivedRows;
  const filtered = useMemo(() => applyFilters(tabRows), [tabRows, search, filters]); // eslint-disable-line react-hooks/exhaustive-deps
  const anyFilter = !!search.trim() || FILTER_DEFS.some((d) => filterActive(d, filters[d.key]));

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE), [filtered, safePage]);

  const monthGroups = useMemo(() => groupByMonth(pageItems), [pageItems]);

  const sentGroups = useMemo(() => ([
    {
      key: "reminder",
      label: "Due for received reminder · unreceived 3+ weeks",
      cls: "bg-rose-50 text-rose-800",
      icon: true,
      items: pageItems.filter(reminderDue),
    },
    {
      key: "transit",
      label: "In transit · awaiting receipt",
      cls: "bg-summit-glacier text-gray-600",
      items: pageItems.filter((r) => !reminderDue(r)),
    },
  ].filter((g) => g.items.length > 0)), [pageItems]);

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="po-no-access">
        <ClipboardList className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">PO Overview</h1>
        <p className="text-sm text-gray-500 mt-2">You don&apos;t have access to this page. Ask an admin to grant it in Admin Settings.</p>
      </div>
    );
  }

  const canDelete = (r) => r.created_by === user.id || user.role === "admin";
  const leaderboard = savings?.leaderboard || [];

  const emptyMsg = anyFilter
    ? "No POs match your search / filters."
    : tab === "open"
      ? "No open POs — click \u201CAdd PO\u201D to start the sheet."
      : tab === "sent"
        ? "No sent POs awaiting receipt — toggle a PO\u2019s Sent to Y and it moves here."
        : "No received POs yet — toggle a PO\u2019s Received to Y and it moves here.";

  const tabBtn = (key, label, count, extra = null) => (
    <button
      onClick={() => setTab(key)}
      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === key ? "bg-summit-primary text-white" : "bg-white border border-summit-hairline text-gray-600 hover:bg-summit-glacier"}`}
      data-testid={`po-tab-${key}`}
    >
      {label} · {count}
      {extra}
    </button>
  );

  const Row = ({ r }) => (
    <tr className={`border-b border-summit-hairline hover:bg-summit-glacier/30 ${reminderDue(r) ? "bg-rose-50/40" : ""}`} data-testid={`po-row-${r.id}`}>
      <td className="px-2 py-1.5">
        {r.sent === "Y" ? (
          <span className="inline-flex items-center gap-1 font-mono text-xs text-gray-500 px-1" data-testid={`po-date-${r.id}`} title="Date is locked once the PO is sent">
            <Lock className="w-3 h-3 text-gray-300" /> {r.date}
          </span>
        ) : (
          <input
            type="date"
            value={r.date || ""}
            onChange={(e) => e.target.value && saveCell(r, { date: e.target.value })}
            className="w-full bg-transparent font-mono text-xs text-gray-600 px-1 py-1 rounded hover:bg-summit-glacier/60 cursor-pointer"
            data-testid={`po-date-${r.id}`}
          />
        )}
      </td>
      <td className="px-1 py-1.5"><TextCell row={r} field="po" onSave={saveCell} /></td>
      <td className="px-2 py-1.5"><YNCell row={r} field="exchange" onSave={saveCell} /></td>
      <td className="px-1 py-1.5"><TextCell row={r} field="vendor" onSave={saveCell} /></td>
      <td className="px-1 py-1.5"><TextCell row={r} field="base" onSave={saveCell} /></td>
      <td className="px-1 py-1.5"><TextCell row={r} field="description" onSave={saveCell} /></td>
      <td className="px-2 py-1.5"><YNCell row={r} field="approve" onSave={saveCell} /></td>
      <td className="px-2 py-1.5"><YNCell row={r} field="sent" onSave={saveCell} /></td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <YNCell row={r} field="received" onSave={saveCell} />
        {reminderDue(r) && (
          <>
            <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 font-mono text-[9px] uppercase tracking-widest" data-testid={`po-reminder-${r.id}`} title={`Unreceived for ${ageDays(r)} days`}>
              <BellRing className="w-2.5 h-2.5" />{ageDays(r)}d
            </span>
            <button
              className="ml-1 p-1 rounded-md text-rose-700 hover:bg-rose-100 align-middle"
              onClick={() => followUp(r)}
              data-testid={`po-followup-${r.id}`}
              title="Followed up with vendor — push the reminder 2 weeks"
            >
              <CheckCheck className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        {snoozed(r) && r.received !== "Y" && (
          <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 font-mono text-[9px] uppercase tracking-widest" data-testid={`po-snoozed-${r.id}`} title={`Followed up${r.followed_up_by_name ? ` by ${r.followed_up_by_name}` : ""} — reminder due again ${r.follow_up_until}`}>
            <CheckCheck className="w-2.5 h-2.5" /> {r.follow_up_until}
          </span>
        )}
      </td>
      <td className="px-1 py-1.5">
        <div className="flex items-center gap-0.5 min-w-0">
          <ReducedCell row={r} onSave={saveReduction} />
          {r.reduced_cost > 0 && (
            <AttachmentClip row={r} onChanged={(nr) => setRows((rs) => (rs || []).map((x) => (x.id === nr.id ? nr : x)))} />
          )}
        </div>
      </td>
      <td className="px-2 py-1.5">
        {r.created_by === user.id ? (
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400" data-testid={`po-owner-${r.id}`}>You</span>
        ) : (
          <span className="inline-flex max-w-full truncate px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-mono text-[10px] uppercase tracking-widest" data-testid={`po-owner-${r.id}`} title={`Raised by ${r.created_by_name}`}>
            {r.created_by_name}
          </span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {canDelete(r) && (
          <button
            className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50"
            onClick={() => remove(r)}
            data-testid={`po-delete-${r.id}`}
            title="Delete row"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );

  const monthBody = monthGroups.map(([ym, group]) => (
    <React.Fragment key={ym}>
      <tr className="bg-summit-glacier" data-testid={`po-month-${ym}`}>
        <td colSpan={COLS} className="px-3 py-1.5 font-display text-xs uppercase tracking-widest text-gray-600">
          {ym === "Unknown" ? "No date" : monthLabel(ym)} ({group.length})
        </td>
      </tr>
      {group.map((r) => <Row key={r.id} r={r} />)}
    </React.Fragment>
  ));

  return (
    <div className="w-full mx-auto px-4 sm:px-[10%] py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Stores · MST</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-1" data-testid="po-heading">PO Overview</h1>
          <p className="text-sm text-gray-500 mt-1">Click any cell to edit — changes save automatically. You see your own POs, plus your reports&apos; and their reports&apos;.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800" onClick={() => setMelOpen(true)} data-testid="po-mel-btn">
            <FileWarning className="w-4 h-4 mr-1.5" /> MEL
          </Button>
          <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => setAogOpen(true)} data-testid="po-aog-btn">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> AOG
          </Button>
          {monthOptions.length > 0 && (
            <>
              <select
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
                className="h-9 px-2 rounded-md border border-summit-hairline bg-white text-sm"
                data-testid="po-export-month"
              >
                {monthOptions.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
              <Button variant="outline" onClick={doExport} data-testid="po-export-btn">
                <Download className="w-4 h-4 mr-1.5" /> Export
              </Button>
            </>
          )}
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={addRow} disabled={busy} data-testid="po-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add PO
          </Button>
        </div>
      </div>

      <div className="mt-6 border border-summit-hairline rounded-lg bg-white grid grid-cols-2 lg:grid-cols-4 divide-x divide-summit-hairline overflow-hidden" data-testid="po-summary-strip">
        <StatCell testid="po-summary-raised" icon={<PackageCheck className="w-3.5 h-3.5" />} label="PO raised" value={(rows || []).length} breakdown={raisedByUser} />
        <StatCell testid="po-summary-notreceived" icon={<PackageX className="w-3.5 h-3.5" />} label="Not received" value={notReceived} tone="amber" breakdown={notReceivedByUser} />
        <StatCell testid="po-summary-reminder" icon={<BellRing className="w-3.5 h-3.5" />} label="Reminder due" value={reminderCount} tone="rose" breakdown={reminderByUser} />
        <Popover>
          <PopoverTrigger asChild>
            <button className="px-4 py-2.5 text-left hover:bg-summit-glacier/40 transition-colors" data-testid="po-summary-reduced" title="Click for savings leaderboard">
              <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-emerald-700">
                <TrendingDown className="w-3.5 h-3.5" /> Cost reduced
              </div>
              <div className="font-display text-xl font-semibold leading-tight mt-0.5 text-emerald-800 truncate" data-testid="po-reduced-team">{fmtTotals(savings?.team)}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400 mt-0.5 truncate" data-testid="po-reduced-mine">You: {fmtTotals(savings?.mine)}</div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="end" data-testid="po-leaderboard">
            <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-gray-500 mb-1.5">
              <Trophy className="w-3 h-3 text-amber-500" /> Savings leaderboard
            </div>
            {leaderboard.length === 0 ? (
              <p className="text-xs text-gray-400">{tierMsg(0)}</p>
            ) : leaderboard.map((l, i) => (
              <div key={l.user_id || i} className={`flex items-center gap-2 px-1.5 py-1 rounded ${l.user_id === user.id ? "bg-emerald-50" : ""}`} data-testid={`po-leader-row-${i + 1}`}>
                <span className={`w-5 h-5 shrink-0 inline-flex items-center justify-center rounded-full font-mono text-[10px] font-bold ${i === 0 ? "bg-amber-100 text-amber-800" : i === 1 ? "bg-gray-200 text-gray-700" : i === 2 ? "bg-orange-100 text-orange-800" : "bg-gray-100 text-gray-500"}`}>
                  {i + 1}
                </span>
                <span className="text-xs font-medium truncate">{l.user_id === user.id ? "You" : l.name}</span>
                <span className="ml-auto font-mono text-[11px] text-emerald-700 font-semibold whitespace-nowrap">−{fmtTotals(l.totals)}</span>
              </div>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        {tabBtn("open", "Open", openRows.length)}
        {tabBtn("sent", "Sent", sentRows.length, reminderCount > 0 ? (
          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 font-mono text-[9px] uppercase tracking-widest" data-testid="po-sent-reminder-pill">
            {reminderCount} due
          </span>
        ) : null)}
        {tabBtn("received", "Received", receivedRows.length)}
        {tab === "sent" && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400" data-testid="po-sent-note">
            POs marked Sent — flagged after {REMINDER_DAYS} days unreceived
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {anyFilter && (
            <Button size="sm" variant="ghost" className="h-8 text-xs text-gray-500" onClick={() => { setSearch(""); setFilters({}); }} data-testid="po-clear-filters">
              <X className="w-3.5 h-3.5 mr-1" /> Clear filters
            </Button>
          )}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="h-8 pl-8 w-56 text-sm" placeholder="Search POs…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="po-search" />
          </div>
        </div>
      </div>

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-hidden">
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="bg-summit-canvas border-b border-summit-hairline">
              {TABLE_COLS.map((col) => (
                <th key={col.label || "actions"} className={`text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 ${col.width}`}>
                  {col.label}
                  {col.keys.length > 0 && (
                    <HeaderFilterPopover
                      col={col}
                      filterDefs={FILTER_DEFS}
                      filters={filters}
                      setFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
                      nameOptions={nameOptions}
                      testPrefix="po"
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="po-table-body">
            {rows === null ? (
              <tr><td colSpan={COLS} className="px-3 py-8 text-center text-gray-400 text-sm">Loading…</td></tr>
            ) : pageItems.length === 0 ? (
              <tr><td colSpan={COLS} className="px-3 py-8 text-center text-gray-400 text-sm" data-testid="po-empty">{emptyMsg}</td></tr>
            ) : tab === "sent" ? (
              sentGroups.map((g) => (
                <React.Fragment key={g.key}>
                  <tr className={g.cls} data-testid={`po-group-${g.key}`}>
                    <td colSpan={COLS} className="px-3 py-1.5 font-display text-xs uppercase tracking-widest">
                      {g.icon && <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                      {g.key === "transit" && <Truck className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                      {g.label} ({g.items.length})
                    </td>
                  </tr>
                  {g.items.map((r) => <Row key={r.id} r={r} />)}
                </React.Fragment>
              ))
            ) : (
              monthBody
            )}
          </tbody>
        </table>
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 border-t border-summit-hairline bg-summit-canvas/60 px-3 py-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500" data-testid="po-page-label">
              Page {safePage} of {totalPages} · {filtered.length} POs
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="po-page-prev">Previous</Button>
              <Button size="sm" variant="outline" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="po-page-next">Next</Button>
            </div>
          </div>
        )}
      </div>

      <MelUpdateDialog open={melOpen} onOpenChange={setMelOpen} />
      <AogAddDialog open={aogOpen} onOpenChange={setAogOpen} />
    </div>
  );
}
